/**
 * Impresión de FAC / NC / ND / NOTA / CAM. Ruta /imprimir/:tipo/:id sin layout de app; nunca imprime la pantalla.
 *
 * - Factura, nota de crédito y nota de débito: papel PREIMPRESO de la imprenta, solo lo fiscal y solo Bs
 *   (`FormatoFiscal`, plantilla por sucursal).
 * - Nota de entrega y cambio: hoja en blanco con el membrete de los dos logos, todo en Bs y el referencial en
 *   dólares al final, con las leyendas de cambios, cortes y retiro (respuestas de la clienta 2026-10-09). Cada combo o
 *   receta lleva una cabecera con su nombre y su medida; la factura no (solo los renglones de productos, spec 20).
 *
 * Datos con tasas CONGELADAS: jamás se recalcula. Sin marca de copia en las reimpresiones (quedan en auditoría).
 */
import { formatearCantidad, formatearTasa, METODO_PAGO_ETIQUETA, Money, type DocumentoDTO, type EmpresaImpresionDTO } from '@mg/shared';
import { Fragment, useEffect } from 'react';
import { useParams } from 'react-router';
import { Leyendas, Membrete, useEmpresaImpresion } from '@/componentes/Impresion.tsx';
import { useRegistroImpresion } from '@/lib/impresion.ts';
import { formatearFecha } from '@/lib/utils.ts';
import { cabeceraDeCombo, tituloCombo } from '@/modulos/combos/documento.ts';
import { registrarImpresion, useDocumento } from './api.ts';
import { FormatoFiscal, horaEmision, LETRA_MATRIZ, MATRIZ_INCLINACION, MATRIZ_TRAZO_PX } from './FormatoFiscal.tsx';

const registrarCopia = (id: string) => registrarImpresion(id).then((r) => r.copia);

export function Imprimir() {
  const { id, tipo } = useParams<{ id: string; tipo: string }>();
  const doc = useDocumento(id ?? null);
  // Cambio directo: la nota de despacho ligada al CAMBIO se imprime en la misma hoja
  const despachoId = doc.data?.tipo === 'CAM' ? (doc.data.derivados.find((x) => x.tipo === 'NOTA')?.id ?? null) : null;
  const despacho = useDocumento(despachoId);
  const empresa = useEmpresaImpresion();
  // Un número anulado por hoja dañada no se imprime: la venta sigue en el documento reemitido. La impresión se anota
  // solo después de saber que el documento está vivo; abrir la hoja de una fila anulada no cuenta como impresión
  // (re-auditoría 2026-10-18, ronda 2, hallazgo 1-H4)
  const anulado = doc.data?.estado === 'anulado';
  const copia = useRegistroImpresion(doc.data && !anulado ? id : undefined, registrarCopia);
  const listo = !anulado && !!doc.data && !!empresa.data && copia !== null && (!despachoId || !!despacho.data);

  useEffect(() => {
    if (listo) {
      const t = setTimeout(() => window.print(), 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [listo]);

  if (doc.isLoading) return <p style={{ padding: 24, fontFamily: 'Georgia, serif' }}>Preparando {tipo}…</p>;
  if (doc.data && anulado) return <p style={{ padding: 24, fontFamily: 'Georgia, serif' }} data-testid="formato-anulado">{doc.data.numero_formateado} está ANULADA por hoja dañada: no se imprime.{doc.data.reemitido_como ? ` La venta sigue en ${doc.data.reemitido_como.numero_formateado}.` : ''}</p>;
  if (empresa.isLoading || (doc.data && copia === null) || (despachoId && despacho.isLoading)) return <p style={{ padding: 24, fontFamily: 'Georgia, serif' }}>Preparando {tipo}…</p>;
  if (!doc.data || !empresa.data) return <p style={{ padding: 24 }}>No se pudo cargar el documento.</p>;
  return <FormatoDocumento d={doc.data} e={empresa.data} despacho={despacho.data ?? null} reimpresion={copia === true} />;
}

export function FormatoDocumento({ d, e, despacho = null, reimpresion = false }: { d: DocumentoDTO; e: EmpresaImpresionDTO; despacho?: DocumentoDTO | null; reimpresion?: boolean }) {
  if (d.tipo === 'FAC' || d.tipo === 'NC' || d.tipo === 'ND') return <FormatoFiscal d={d} e={e} reimpresion={reimpresion} />;
  return <FormatoNota d={d} e={e} despacho={despacho} />;
}

const cant = (v: string) => formatearCantidad(Money.parse(v));
const pagosTexto = (lineas: DocumentoDTO['pagos'][number]['lineas']) =>
  lineas
    .map((l) => {
      const metodo = (METODO_PAGO_ETIQUETA as Record<string, string>)[l.metodo] ?? l.metodo;
      const moneda = l.moneda === 'BS' ? 'Bs' : '$';
      // La etiqueta de algunos métodos ya trae la moneda («Efectivo $»): no se repite
      return `${metodo.endsWith(moneda) ? metodo : `${metodo} ${moneda}`} ${Money.parse(l.monto).toDisplay()}${l.referencia ? ` (${l.referencia})` : ''}`;
    })
    .join(' · ');

/**
 * Renglones en Bs tal como se guardaron (precio × tasa 2 redondeado por unidad, × cantidad): lo que cuadra a mano.
 * Si algún renglón lleva descuento sale la columna «% Dscto» (mismo criterio de la factura): precio × cantidad menos
 * ese porcentaje da el total del renglón.
 */
function TablaLineas({ d, titulo }: { d: DocumentoDTO; titulo?: string | undefined }) {
  const conDescuento = d.lineas.some((l) => Money.parse(l.descuento_pct).esPositivo());
  const columnas = conDescuento ? 6 : 5;
  return (
    <table>
      <thead>
        {titulo ? (
          <tr>
            <th colSpan={columnas} style={{ borderBottom: 'none', paddingTop: 6 }}>{titulo}</th>
          </tr>
        ) : null}
        <tr>
          <th>Código</th>
          <th>Descripción</th>
          <th className="num">Cantidad</th>
          <th className="num">Precio unit. Bs</th>
          {conDescuento ? <th className="num" data-testid="formato-col-descuento">% Dscto</th> : null}
          <th className="num">Total Bs</th>
        </tr>
      </thead>
      <tbody>
        {d.lineas.map((l, i) => {
          const factor = Money.parse(l.unidades_presentacion);
          // Presentaciones completas: unidades ÷ factor (la cantidad guardada va redondeada a 2 decimales y no sirve para saberlo)
          const presentaciones = Money.parse(l.unidades).div(factor.esCero() ? '1' : factor);
          const detalle = factor.gt('1') && presentaciones.decimal.isInteger() ? ` · ${cant(presentaciones.toString())} × ${l.presentacion_nombre}` : '';
          // Combo o receta (spec 20, regla 7): en la nota su nombre y la medida van antes de su primer renglón (en la factura no)
          const combo = cabeceraDeCombo(d.combos, d.lineas, i);
          return (
            <Fragment key={l.id}>
              {combo ? (
                <tr data-testid="formato-combo">
                  <td colSpan={columnas} className="combo-titulo">{tituloCombo(combo)}</td>
                </tr>
              ) : null}
              <tr>
                <td>{l.producto_codigo}</td>
                <td>{l.producto_nombre}{detalle}</td>
                <td className="num" data-testid="formato-linea-cantidad">
                  {cant(l.unidades)} <span style={{ fontSize: 8 }}>{l.producto_unidad}</span>
                </td>
                <td className="num">{Money.parse(l.precio_unitario_bs).toDisplay()}</td>
                {conDescuento ? <td className="num" data-testid="formato-linea-descuento">{Money.parse(l.descuento_pct).esPositivo() ? Money.parse(l.descuento_pct).toDisplay() : ''}</td> : null}
                <td className="num" data-testid="formato-linea-total">{Money.parse(l.subtotal_bs).toDisplay()}</td>
              </tr>
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * Nota de entrega (no fiscal) y cambio: hoja en blanco, carta o media carta según la sucursal.
 * En media carta (Barinas, impresora de matriz de puntos) lleva la letra de la factura: Lucida Console en negrita, que
 * la matricial imprime nítida (respuesta de la dueña del 26-10-2026: «cambia la letra como está la factura»). La hoja
 * mide 8,5 × 5,5 in con los márgenes por dentro, como la factura: con márgenes «Ninguno» nada queda fuera del papel
 * (antes el ancho más los márgenes pasaba de las 8,5 in y la matricial cortaba el borde derecho).
 */
function FormatoNota({ d, e, despacho }: { d: DocumentoDTO; e: EmpresaImpresionDTO; despacho: DocumentoDTO | null }) {
  // La sucursal con factura en media carta imprime la nota y el Cambio en media carta horizontal. Con el papel de la
  // mitad de una hoja carta (Barinas, 2026-11-03) la página es la hoja carta de pie y la nota ocupa sus 5,5 in de arriba
  // (margen de abajo de media hoja): una nota larga sigue en la mitad de arriba de la hoja siguiente, y se corta igual
  // que la factura. Con la página acostada, la impresora la giraba sobre la hoja de pie
  const mediaCarta = d.sucursal_formato_impresion !== 'carta';
  const enMitad = d.sucursal_formato_impresion === 'media_carta_vertical';
  // Los lados, los de la factura de su sucursal (orden del usuario 2026-11-03: se cortaba a la derecha; en Barinas, 4 y
  // 20 mm): así la nota sigue la calibración del papel. Arriba y abajo, los 7 mm de siempre
  const relleno = enMitad ? `7mm ${d.sucursal_plantilla.derecho_mm}mm 7mm ${d.sucursal_plantilla.izquierdo_mm}mm` : '7mm 9mm';
  const titulo = d.tipo === 'CAM' ? 'CAMBIO' : 'NOTA DE ENTREGA';
  const totalLineasBs = Money.sumar(d.lineas.map((l) => Money.parse(l.subtotal_bs)));
  const totalBs = Money.parse(d.total_bs);
  // Descuento global en Bs: lo que separa la suma de los renglones (ya con su descuento) del total
  const descuentoBs = Money.parse(d.descuento_pct).esPositivo() ? totalLineasBs.sub(totalBs).redondear2() : Money.cero();
  const sede = { nombre: d.sucursal_nombre, direccion_fiscal: d.sucursal_direccion_fiscal, telefono: d.sucursal_telefono };
  return (
    <div className={`formato ${mediaCarta ? 'media' : 'carta'}`} data-testid="formato-documento" data-formato={d.sucursal_formato_impresion}>
      <style>{`
        @page { size: ${mediaCarta && !enMitad ? '8.5in 5.5in' : 'letter'}; margin: ${!mediaCarta ? '12mm' : enMitad ? '0 0 5.5in 0' : '0'}; }
        html, body { background: #fff; margin: 0; }
        .formato { font-family: ${mediaCarta ? LETRA_MATRIZ : 'Arial, Helvetica, sans-serif'}; color: #000; font-size: ${mediaCarta ? '10pt' : '11px'}; width: ${mediaCarta ? '8.5in' : '7.5in'}; margin: 0 auto; padding: ${mediaCarta ? relleno : '4px'}; box-sizing: ${mediaCarta ? 'border-box' : 'content-box'}; position: relative; }
        /* Cada hoja de una nota larga repite los márgenes de adentro */
        /* Media carta (Barinas, matricial): la negrita de la factura, dibujada con un contorno (FormatoFiscal.tsx). Los 0,2 pt de
           separación reponen el ancho que daba la negrita simulada, para que nada se mueva */
        .formato.media { font-weight: normal; letter-spacing: 0.2pt; -webkit-text-stroke: ${MATRIZ_TRAZO_PX}px #000; box-decoration-break: clone; -webkit-box-decoration-break: clone; }
        .formato.media th, .formato.media b, .formato.media .combo-titulo { font-weight: inherit; }
        .formato.media .ayuda-impresion { -webkit-text-stroke: 0; letter-spacing: normal; font-family: system-ui, sans-serif; font-weight: normal; font-size: 12px; background: #fef3c7; padding: 6px 8px; margin-bottom: 8px; line-height: 1.4; }
        @media print { .formato .ayuda-impresion { display: none; } }
        .formato table { width: 100%; border-collapse: collapse; }
        .formato th, .formato td { padding: 2px 4px; vertical-align: top; }
        .formato th { border-top: 1px solid #000; border-bottom: 1px solid #000; text-align: left; font-weight: bold; }
        .formato .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .formato .tot td { padding: 1px 4px; }
        .formato .legal { font-size: ${mediaCarta ? '8.5px' : '9.5px'}; margin-top: 6px; }
        .formato .combo-titulo { font-weight: bold; padding-top: 5px; }
        .formato .rot { font-style: italic; }
        .formato.media .rot { font-style: normal; display: inline-block; transform: ${MATRIZ_INCLINACION}; }
        @media screen { body { background: #e5e7eb; } .formato { background: #fff; box-shadow: 0 0 8px rgba(0,0,0,.2); margin: 16px auto; } .formato.carta { padding: 14px; } }
      `}</style>
      {mediaCarta ? (
        <div className="ayuda-impresion" data-testid="formato-nota-ayuda">
          {enMitad
            ? 'Media carta en la mitad de arriba de una hoja carta de pie, como la factura: en el cuadro de impresión, papel «Carta», márgenes «Predeterminados» y escala 100 %. Después se corta por la mitad. No sale en el papel.'
            : 'Media carta horizontal (8,5 × 5,5 in), como la factura: en el cuadro de impresión, papel «Media carta», márgenes «Ninguno» y escala 100 %. No sale en el papel.'}
        </div>
      ) : null}
      <Membrete empresa={e} sede={sede} compacto={mediaCarta} />
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
        <div style={{ flex: 1 }}>
          <div><span className="rot">Cliente :</span> {d.cliente_razon_social}</div>
          <div><span className="rot">Domicilio :</span> {d.cliente_direccion_fiscal || '—'}</div>
          <div>
            <span className="rot">Rif / Cédula :</span> {d.cliente_rif}
            <span className="rot" style={{ marginLeft: 24 }}>Teléfonos :</span> {d.cliente_telefono ?? ''}
          </div>
        </div>
        <div style={{ minWidth: mediaCarta ? 190 : 220 }}>
          <div><b className="rot">{titulo} No. :</b> <b style={{ fontSize: '1.25em' }} data-testid="formato-numero">{d.numero_formateado}</b></div>
          <div><b data-testid="formato-no-fiscal">DOCUMENTO NO FISCAL</b></div>
          <div><span className="rot">Fecha Emisión :</span> {formatearFecha(d.emitido_en)}</div>
          <div><span className="rot">Cond. de pago :</span> {d.condicion === 'credito' ? `CRÉDITO${d.vence_en ? ` (vence ${formatearFecha(d.vence_en)})` : ''}` : 'CONTADO'}</div>
          {/* La hora del sistema en que se emitió, debajo de la condición como en la factura; en una reimpresión sigue
              siendo la de emisión (pedido de la dueña 2026-10-19) */}
          <div><span className="rot">Hora Emisión :</span> <span data-testid="formato-hora">{horaEmision(d.emitido_en)}</span></div>
          {d.documento_origen_numero ? <div><span className="rot">Ref. :</span> {d.documento_origen_numero}</div> : null}
        </div>
      </div>
      <TablaLineas d={d} titulo={despacho ? 'RECIBIMOS (devuelto por el cliente)' : undefined} />
      <table style={{ marginTop: 6 }}>
        <tbody>
          {descuentoBs.esPositivo() ? (
            <>
              <tr className="tot">
                <td style={{ width: '58%' }} />
                <td className="num">Total renglones Bs</td>
                <td className="num" data-testid="formato-subtotal-bs">{totalLineasBs.toDisplay()}</td>
              </tr>
              <tr className="tot">
                <td />
                <td className="num">Descuento {Money.parse(d.descuento_pct).toString()}% en Bs</td>
                <td className="num" data-testid="formato-descuento-bs">−{descuentoBs.toDisplay()}</td>
              </tr>
            </>
          ) : null}
          <tr className="tot">
            <td style={{ width: '58%' }} />
            <td className="num"><b>{despacho ? 'CRÉDITO' : 'TOTAL'} EN Bs</b></td>
            <td className="num" style={{ borderTop: '1px solid #000' }}><b data-testid="formato-total-bs">Bs {totalBs.toDisplay()}</b></td>
          </tr>
          {/* Respuesta de la clienta (P14): todo en bolívares y al final el referencial en dólares */}
          <tr className="tot">
            <td />
            <td className="num">Referencial en dólares</td>
            <td className="num" data-testid="formato-total-usd">$ {Money.parse(d.total).toDisplay()}</td>
          </tr>
        </tbody>
      </table>
      {despacho ? <SeccionDespacho cambio={d} despacho={despacho} /> : null}
      {d.tipo === 'CAM' && d.documento_origen_numero ? <div className="legal" data-testid="formato-cambio-leyenda">Documento interno: no anula ni modifica {d.documento_origen_numero}.</div> : null}
      {/* Sin la línea de «Pagos: …» ni su vuelto al pie: la nota no la trae (pedido de la dueña 2026-10-19) */}
      <Leyendas textos={[e.leyendas.cambios, e.leyendas.cortes, e.leyendas.retiro]} tamano={mediaCarta ? 8.5 : 9.5} />
      <div className="legal" style={{ display: 'flex', justifyContent: 'space-between', marginTop: mediaCarta ? 12 : 22 }}>
        <span>Atendido por: {d.cajero_nombre}</span>
        <span>Recibí conforme: ______________________</span>
      </div>
    </div>
  );
}

/** Cambio directo: lo que se despacha al cliente (nota ligada) y la diferencia (cobrada o a favor). */
function SeccionDespacho({ cambio, despacho }: { cambio: DocumentoDTO; despacho: DocumentoDTO }) {
  // Crédito del CAMBIO = su por_cobrar (en facturas base25 ya descuenta las retenciones proporcionales)
  const credito = Money.parse(cambio.por_cobrar);
  const cargo = Money.parse(despacho.total);
  const diferencia = cargo.sub(credito);
  const tasaD = despacho.tasa_aplicada;
  const pagosExtra = despacho.pagos.flatMap((x) => x.lineas).filter((l) => l.metodo !== 'cambio');
  return (
    <div data-testid="formato-despacho" style={{ marginTop: 8 }}>
      <TablaLineas d={despacho} titulo={`DESPACHAMOS (entregado al cliente · ${despacho.numero_formateado})`} />
      <table>
        <tbody>
          <tr className="tot">
            <td className="legal" style={{ width: '58%' }}>Precios y tasa del día del cambio: Bs {formatearTasa(tasaD)} por $.</td>
            <td className="num"><b>DESPACHO</b></td>
            <td className="num"><b>Bs {Money.parse(despacho.total_bs).toDisplay()}</b> <span style={{ fontSize: 9 }}>($ {cargo.toDisplay()})</span></td>
          </tr>
          <tr>
            <td />
            <td className="num"><b>{diferencia.esPositivo() ? 'DIFERENCIA COBRADA' : diferencia.esNegativo() ? 'A FAVOR DEL CLIENTE' : 'DIFERENCIA'}</b></td>
            <td className="num" data-testid="formato-diferencia"><b>$ {diferencia.abs().toDisplay()}</b></td>
          </tr>
        </tbody>
      </table>
      {pagosExtra.length > 0 ? <div className="legal">Diferencia pagada con: {pagosTexto(pagosExtra)}</div> : null}
    </div>
  );
}
